/**
 * The app's toast stack.
 *
 * The prototype held one `toast` string in App state and rendered one pill; production
 * keeps the pill and drops the "one" — approving two requests in quick succession used
 * to destroy the first confirmation before anyone read it. Toasts now queue oldest-top,
 * newest-bottom, each with its own timer, and each dismissible by hand.
 *
 * `tone` is the other addition. A toast is the only feedback six screens give for a
 * failed mutation, so the host has to be able to say *failed*: `error` renders red and
 * announces assertively, `success` and `info` stay polite. Callers that pass no tone
 * keep the prototype's neutral-confirmation behaviour.
 *
 * `undo` is a first-class field rather than a generic action, because the only
 * reversible thing in this product is an audit entry and the wording is fixed.
 */
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";
import { Toast, toastDuration, type ToastTone } from "@/components/ui";

/** Re-exported for callers that want the prototype's confirmation window explicitly. */
export { TOAST_DURATION_MS, TOAST_ERROR_DURATION_MS, TOAST_UNDO_DURATION_MS } from "@/components/ui";

/**
 * Matches the prototype's 2.2–2.8 s window. Errors and Undo offers live longer —
 * {@link toastDuration} decides, and an explicit `duration` overrides it.
 */
export const TOAST_MS = 2500;

/**
 * Beyond three the stack is taller than the content it is confirming. The oldest is
 * dropped, not the newest: the most recent action is the one the user is watching for.
 */
const MAX_VISIBLE = 3;

export interface ToastRequest {
  text: ReactNode;
  /** Defaults to "success" — the prototype's neutral confirmation pill. */
  tone?: ToastTone;
  /** Label for the trailing button. Only rendered when `onUndo` is supplied too. */
  action?: string;
  onUndo?: () => void;
  /** Override the tone's default life, in ms. */
  duration?: number;
}

interface ToastState extends ToastRequest {
  id: number;
}

interface ToastContextValue {
  showToast: (request: ToastRequest) => void;
  /** Dismiss one toast, or every toast when called with no id. */
  dismissToast: (id?: number) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

let nextToastId = 0;

export function ToastHost({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<readonly ToastState[]>([]);

  const showToast = useCallback((request: ToastRequest) => {
    // A counter rather than Date.now(): two toasts fired in the same millisecond would
    // otherwise share a key, and React would reuse one pill for both.
    const id = (nextToastId += 1);
    setToasts((current) => [...current, { ...request, id }].slice(-MAX_VISIBLE));
  }, []);

  const dismissToast = useCallback((id?: number) => {
    setToasts((current) => (id === undefined ? [] : current.filter((toast) => toast.id !== id)));
  }, []);

  const value = useMemo(() => ({ showToast, dismissToast }), [showToast, dismissToast]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      {toasts.length > 0 ? (
        <div className="ui-toast-stack">
          {toasts.map((toast) => {
            const tone = toast.tone ?? "success";
            const action = toast.onUndo ? (toast.action ?? "Undo") : undefined;
            return (
              <Toast
                key={toast.id}
                text={toast.text}
                tone={tone}
                action={action}
                onAction={
                  toast.onUndo
                    ? () => {
                        toast.onUndo?.();
                        dismissToast(toast.id);
                      }
                    : undefined
                }
                onDismiss={() => dismissToast(toast.id)}
                duration={toast.duration ?? toastDuration(tone, Boolean(action))}
              />
            );
          })}
        </div>
      ) : null}
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const context = useContext(ToastContext);
  if (!context) throw new Error("useToast must be used inside <ToastHost>.");
  return context;
}

export default ToastHost;
